import React from "react";
import { Card, IconButton, Tooltip, Typography } from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import Div from "@jumbo/shared/Div";
import { useTranslation } from "react-i18next";

const VOUCHERS_I18N = "pages.dsokDetails.accessSystem.vouchers";

/**
 * One voucher series row — visual pattern aligned with DSOKProductItem (labels + body1, px:3).
 * Supports API rows with productId; legacy rows may still have ticketProduct / vouchers.
 */
const VoucherSeriesListItem = ({
  item,
  ticketTypeLabel,
  onOpen,
  formatValidUntilDisplay,
  resolveStatusLabel,
}) => {
  const { t } = useTranslation();
  const productDisplay =
    ticketTypeLabel != null && String(ticketTypeLabel).trim() !== ""
      ? String(ticketTypeLabel)
      : "—";

  return (
    <Card
      sx={{ mb: 1, cursor: "pointer" }}
      onClick={() => onOpen(item.id)}
    >
      <Div
        sx={{
          px: 3,
          py: 1.5,
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { xs: "stretch", md: "center" },
          justifyContent: "space-between",
          flexWrap: "wrap",
          rowGap: { xs: 1.5, md: 0 },
        }}
      >
        <Div sx={{ flex: { xs: "1 1 100%", md: 1.2 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t(`${VOUCHERS_I18N}.series.columns.name`, {
              defaultValue: "Name",
            })}
          </Typography>
          <Typography variant="body1">{item.name}</Typography>
          <Div sx={{ mt: 0.25 }}>
            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ fontSize: "0.7rem", lineHeight: 1.2 }}
            >
              ID: {item.id}
            </Typography>
          </Div>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 1 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t(`${VOUCHERS_I18N}.series.columns.ticketType`, {
              defaultValue: "Typ lístka",
            })}
          </Typography>
          <Typography variant="body1">{productDisplay}</Typography>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 0.8 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t(`${VOUCHERS_I18N}.series.columns.validUntil`, {
              defaultValue: "Valid until",
            })}
          </Typography>
          <Typography variant="body1">
            {formatValidUntilDisplay(item.validUntil)}
          </Typography>
        </Div>

        <Div sx={{ flex: { xs: "1 1 100%", md: 0.6 }, minWidth: 0, px: 1 }}>
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t(`${VOUCHERS_I18N}.series.columns.status`, {
              defaultValue: "Status",
            })}
          </Typography>
          <Typography variant="body1">{resolveStatusLabel(item.status)}</Typography>
        </Div>

        <Div
          sx={{
            flex: { xs: "1 1 auto", md: 0 },
            px: 1,
            alignSelf: { xs: "flex-end", md: "center" },
          }}
        >
          <Typography
            fontSize="12px"
            variant="h6"
            color="text.secondary"
            mb={0.25}
            sx={{ display: { xs: "none", md: "block" } }}
          >
            {t("pages.presentation.actions")}
          </Typography>
          <Div
            sx={{
              display: "flex",
              flexDirection: "row",
              alignItems: "center",
              gap: 0.5,
              minWidth: 40,
            }}
          >
            <Tooltip title={t("buttons.edit")} placement="top-start">
              <IconButton
                size="small"
                onClick={(e) => {
                  e.stopPropagation();
                  onOpen(item.id);
                }}
                aria-label={t("buttons.edit")}
              >
                <EditIcon />
              </IconButton>
            </Tooltip>
          </Div>
        </Div>
      </Div>
    </Card>
  );
};

export default VoucherSeriesListItem;
